import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Header from "../../components/ui/Header";
import Sidebar from "../../components/ui/Sidebar";
import Breadcrumb from "../../components/ui/Breadcrumb";
import Button from "../../components/ui/Button";
import Icon from "../../components/AppIcon";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { canAccess } from "../../utils/permissions";
import {
  calculateAgeBreakdown,
  formatAgeBreakdown,
} from "../../utils/ageCalculator";

const PatientManagement = () => {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [patients, setPatients] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({
    total: 0,
    perPage: 10,
    currentPage: 1,
    totalPages: 1,
  });

  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("add"); // "add" | "edit"
  const [selectedPatient, setSelectedPatient] = useState(null);

  const [formData, setFormData] = useState({
    fullName: "",
    contactNo: "",
    age: "",
    gender: "",
    email: "",
    dob: "",
    address: "",
    passport: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  const apiBase = import.meta.env.VITE_API_BASE_URL;

  const genderOptions = useMemo(
    () => [
      { value: "", label: "Select Gender" },
      { value: "Male", label: "Male" },
      { value: "Female", label: "Female" },
      { value: "Other", label: "Other" },
    ],
    [],
  );

  const sourceOptions = useMemo(
    () => [
      { value: "all", label: "All Sources" },
      { value: "admin", label: "Backend" },
      { value: "app", label: "Online" },
    ],
    [],
  );

  const fetchPatients = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const res = await axios.get(`${apiBase}/api/patients`, {
        params: {
          page,
          limit: 10,
          search: searchTerm?.trim() || "",
          source: sourceFilter,
        },
      });

      setPatients(res.data?.patients || []);
      setMeta(
        res.data?.meta || {
          total: 0,
          perPage: 10,
          currentPage: page,
          totalPages: 1,
        },
      );
    } catch (err) {
      console.error("Error fetching patients:", err);
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Failed to load patients. Please try again.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(() => {
      fetchPatients();
    }, 450);

    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, page, sourceFilter]);

  const resetForm = () => {
    setFormData({
      fullName: "",
      contactNo: "",
      age: "",
      gender: "",
      email: "",
      dob: "",
      address: "",
      passport: "",
    });
    setFormError("");
    setSelectedPatient(null);
    setModalMode("add");
  };

  const openAddModal = () => {
    resetForm();
    setModalMode("add");
    setShowModal(true);
  };

  const openEditModal = (patient) => {
    setSelectedPatient(patient);
    setModalMode("edit");

    setFormData({
      fullName: patient?.fullName || "",
      contactNo: patient?.contactNo || "",
      age: patient?.age ?? "",
      gender: patient?.gender || "",
      email: patient?.email || "",
      dob: patient?.dob ? String(patient.dob).split("T")[0] : "",
      address: patient?.address || "",
      passport: patient?.passport || "",
    });

    setFormError("");
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "age") {
      const cleaned = value.replace(/[^\d]/g, "");
      setFormData((prev) => ({ ...prev, age: cleaned }));
      return;
    }

    if (name === "contactNo") {
      const cleaned = value.replace(/[^\d]/g, "").slice(0, 10);
      setFormData((prev) => ({ ...prev, contactNo: cleaned }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSourceChange = (e) => {
    setSourceFilter(e.target.value);
    setPage(1);
  };

  const validateForm = () => {
    const nameOk = formData.fullName?.trim().length > 0;
    const contactOk = formData.contactNo?.trim().length >= 10;
    const ageNum = Number(formData.age);
    const ageOk = Number.isFinite(ageNum) && ageNum >= 0 && ageNum < 130;
    const genderOk = formData.gender?.trim().length > 0;

    if (!nameOk || !contactOk || !ageOk || !genderOk) {
      setFormError("Name, Contact No, Age, and Gender are mandatory.");
      return false;
    }
    setFormError("");
    return true;
  };

  const handleSavePatient = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setIsSubmitting(true);
      setFormError("");

      const payload = {
        fullName: formData.fullName?.trim(),
        contactNo: formData.contactNo?.trim(),
        age: Number(formData.age),
        gender: formData.gender,
        ...(formData.email?.trim() ? { email: formData.email.trim() } : {}),
        ...(formData.address?.trim()
          ? { address: formData.address.trim() }
          : {}),
        ...(formData.passport?.trim()
          ? { passport: formData.passport.trim() }
          : {}),
      };

      if (formData.dob) {
        payload.dob = formData.dob;
      } else {
        const ageNum = Number(formData.age);
        if (Number.isFinite(ageNum) && ageNum >= 0) {
          const birthYear = new Date().getFullYear() - ageNum;
          payload.dob = `${birthYear}-01-01`;
        }
      }

      if (modalMode === "add") {
        await axios.post(`${apiBase}/api/patients`, payload);
      } else {
        const id = selectedPatient?.id;
        if (!id) throw new Error("Patient ID missing for update.");
        await axios.put(`${apiBase}/api/patients/${id}`, payload);
      }

      closeModal();
      setPage(1);
      await fetchPatients();
    } catch (err) {
      console.error("Save patient error:", err);
      setFormError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Failed to save patient. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchPatients();
  };

  const handleViewDetails = (patientId) => {
    navigate(`/patients/${patientId}`);
  };

  const handleMenuToggle = () => setSidebarOpen((v) => !v);
  const handleMenuToggle1 = () => setSidebarOpen((v) => !v);
  const handleSidebarClose = () => setSidebarOpen(false);

  const goToPage = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= meta.totalPages) {
      setPage(pageNumber);
    }
  };

  const getPatientType = (patient) => {
    if (
      patient.isPrimary === true ||
      patient.relationship === "self" ||
      patient.relationship === "Self"
    ) {
      return {
        type: "Self",
        color: "bg-green-100 text-green-800 border-green-200",
        textColor: "text-green-700",
        icon: "UserCheck",
      };
    } else {
      return {
        type: "Under Primary",
        color: "bg-blue-100 text-blue-800 border-blue-200",
        textColor: "text-blue-700",
        icon: "Users",
        primaryInfo: patient.primary
          ? `Under: ${patient.primary.fullName}`
          : "Linked to primary",
      };
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header
        onMenuToggle={handleMenuToggle}
        isMenuOpen={sidebarOpen}
        onCollapseToggle={handleMenuToggle1}
      />
      <Sidebar
        isOpen={sidebarOpen}
        onClose={handleSidebarClose}
        isCollapsed={sidebarCollapsed}
        onCollapseToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
      />

      <main
        className={`pt-16 transition-all duration-300 ${
          sidebarCollapsed ? "lg:ml-16" : "lg:ml-60"
        }`}
      >
        <div className="p-4">
          <Breadcrumb />

          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
            <div>
              <h1 className="text-xl font-bold text-gray-900 mb-1">
                Patient Management
              </h1>
              <p className="text-xs text-gray-600">
                Manage patient records and profiles
              </p>
            </div>

            <Button
              onClick={openAddModal}
              disabled={!canAccess(currentUser, "canCreate")}
              className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-3 py-2"
            >
              <Icon name="Plus" size={14} className="mr-1" />
              Add Patient
            </Button>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm mb-4">
            <form
              onSubmit={handleSearch}
              className="flex flex-col sm:flex-row gap-3"
            >
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Search Patients
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                    <Icon name="Search" size={14} className="text-gray-400" />
                  </div>
                  <input
                    type="text"
                    placeholder="Search by name, email, contact, passport..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="block w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md bg-white placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-xs"
                  />
                </div>
              </div>

              <div className="sm:w-56">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Filter by Source
                </label>
                <select
                  value={sourceFilter}
                  onChange={handleSourceChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-xs"
                >
                  {sourceOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="sm:self-end">
                <Button
                  type="submit"
                  variant="solid"
                  size="sm"
                  iconName="Search"
                  className="bg-blue-600 hover:bg-blue-700 text-white h-[34px] px-4 text-xs"
                >
                  Search
                </Button>
              </div>
            </form>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">
                    Patient Records
                  </h3>
                  <p className="text-xs text-gray-600 mt-0.5">
                    {meta.total || 0} patients found • Page {meta.currentPage} of{" "}
                    {meta.totalPages}
                  </p>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700 uppercase tracking-wider w-12">
                      SL No
                    </th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700 uppercase tracking-wider">
                      Contact No
                    </th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700 uppercase tracking-wider">
                      Age
                    </th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700 uppercase tracking-wider">
                      Gender
                    </th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-3 py-2 text-right font-semibold text-gray-700 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody className="bg-white divide-y divide-gray-200">
                  {isLoading ? (
                    <tr>
                      <td colSpan="9" className="px-4 py-6 text-center">
                        <div className="flex justify-center items-center">
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                        </div>
                        <p className="mt-1 text-xs text-gray-500">
                          Loading patients...
                        </p>
                      </td>
                    </tr>
                  ) : error ? (
                    <tr>
                      <td colSpan="9" className="px-4 py-6 text-center">
                        <div className="flex flex-col items-center justify-center">
                          <Icon
                            name="AlertCircle"
                            size={32}
                            className="text-red-400 mb-2"
                          />
                          <p className="text-red-500 font-medium text-xs">
                            Error Loading Patients
                          </p>
                          <p className="text-red-400 text-xs mt-0.5">{error}</p>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={fetchPatients}
                            className="mt-2 text-xs"
                          >
                            Retry
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ) : patients.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="px-4 py-6 text-center">
                        <div className="flex flex-col items-center justify-center">
                          <Icon
                            name="Users"
                            size={32}
                            className="text-gray-400 mb-2"
                          />
                          <p className="text-gray-500 font-medium text-xs">
                            No patients found
                          </p>
                          <p className="text-gray-400 text-xs mt-0.5">
                            {searchTerm || sourceFilter !== "all"
                              ? "Try adjusting your search or source filter"
                              : "No patients have been added yet"}
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    patients.map((patient, index) => {
                      const patientType = getPatientType(patient);

                      return (
                        <tr
                          key={patient.id}
                          className="hover:bg-gray-50 transition-colors duration-150 cursor-pointer"
                        >
                          <td className="px-3 py-2 text-gray-500 font-medium">
                            {(meta.currentPage - 1) * meta.perPage + index + 1}
                          </td>

                          <td className="px-3 py-2">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-7 w-7 bg-blue-100 rounded-md flex items-center justify-center">
                                <Icon
                                  name="User"
                                  size={14}
                                  className="text-blue-600"
                                />
                              </div>
                              <div className="ml-2">
                                <div className="text-xs font-medium text-gray-900">
                                  {patient.fullName || "Unknown"}
                                </div>
                                {patient.source ? (
                                  <div className="text-xs text-gray-500 truncate max-w-[260px]">
                                    {patient.source}
                                  </div>
                                ) : (
                                  <div className="text-xs text-gray-400">-</div>
                                )}
                              </div>
                            </div>
                          </td>

                          <td className="px-3 py-2 text-gray-700">
                            {patient.contactNo || "—"}
                          </td>

                          <td className="px-3 py-2 text-gray-700">
                            {patient.dob
                              ? formatAgeBreakdown(
                                  calculateAgeBreakdown(patient.dob),
                                )
                              : `${patient.age ?? "-"} yrs`}
                          </td>

                          <td className="px-3 py-2 text-gray-700">
                            {patient.gender || "N/A"}
                          </td>

                          <td className="px-3 py-2">
                            <div className="flex flex-col gap-1">
                              <span
                                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${patientType.color}`}
                                title={patientType.primaryInfo || patientType.type}
                              >
                                <Icon
                                  name={patientType.icon}
                                  size={10}
                                  className={`mr-1 ${patientType.textColor}`}
                                />
                                {patientType.type}
                              </span>
                              {patientType.primaryInfo && (
                                <div className="text-[10px] text-gray-500 truncate max-w-[120px]">
                                  {patientType.primaryInfo}
                                </div>
                              )}
                            </div>
                          </td>

                          <td className="px-3 py-2 text-gray-700">
                            {patient.email || "—"}
                          </td>

                          <td className="px-3 py-2">
                            <div className="text-xs text-gray-900">
                              {patient.createdAt
                                ? new Date(patient.createdAt).toLocaleDateString()
                                : "—"}
                            </div>
                            <div className="text-xs text-gray-500">
                              {patient.createdAt
                                ? new Date(patient.createdAt).toLocaleTimeString()
                                : ""}
                            </div>
                          </td>

                          <td className="px-3 py-2">
                            <div className="flex justify-end space-x-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                iconName="Eye"
                                disabled={!canAccess(currentUser, "canEdit")}
                                className="text-blue-600 hover:text-blue-800 border border-blue-200 hover:border-blue-300 text-xs px-2 py-1"
                                onClick={() => handleViewDetails(patient.id)}
                              />
                              <Button
                                variant="ghost"
                                size="sm"
                                iconName="Edit"
                                disabled={!canAccess(currentUser, "canEdit")}
                                className="text-green-600 hover:text-green-800 border border-green-200 hover:border-green-300 text-xs px-2 py-1"
                                onClick={() => openEditModal(patient)}
                              />
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {meta.totalPages > 1 && (
              <div className="px-4 py-3 bg-gray-50 border-t border-gray-200">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                  <div className="mb-3 sm:mb-0">
                    <p className="text-xs text-gray-700">
                      Showing{" "}
                      <span className="font-medium">
                        {(meta.currentPage - 1) * meta.perPage + 1}
                      </span>{" "}
                      to{" "}
                      <span className="font-medium">
                        {Math.min(meta.currentPage * meta.perPage, meta.total)}
                      </span>{" "}
                      of <span className="font-medium">{meta.total}</span>{" "}
                      patients
                    </p>
                  </div>

                  <div className="flex items-center space-x-1">
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="ChevronLeft"
                      onClick={() => goToPage(meta.currentPage - 1)}
                      disabled={meta.currentPage === 1}
                      className="px-2 py-1 text-xs"
                    />

                    {(() => {
                      const pages = [];
                      const maxVisible = 5;
                      let startPage = Math.max(
                        1,
                        meta.currentPage - Math.floor(maxVisible / 2),
                      );
                      let endPage = Math.min(
                        meta.totalPages,
                        startPage + maxVisible - 1,
                      );

                      if (endPage - startPage + 1 < maxVisible) {
                        startPage = Math.max(1, endPage - maxVisible + 1);
                      }

                      if (startPage > 1) {
                        pages.push(
                          <Button
                            key={1}
                            variant="outline"
                            size="sm"
                            onClick={() => goToPage(1)}
                            className={`px-2 py-1 min-w-[28px] text-xs ${
                              meta.currentPage === 1
                                ? "bg-blue-50 text-blue-600 border-blue-200"
                                : ""
                            }`}
                          >
                            1
                          </Button>,
                        );

                        if (startPage > 2) {
                          pages.push(
                            <span
                              key="ellipsis1"
                              className="px-1 text-gray-500 text-xs"
                            >
                              ...
                            </span>,
                          );
                        }
                      }

                      for (let i = startPage; i <= endPage; i++) {
                        pages.push(
                          <Button
                            key={i}
                            variant="outline"
                            size="sm"
                            onClick={() => goToPage(i)}
                            className={`px-2 py-1 min-w-[28px] text-xs ${
                              meta.currentPage === i
                                ? "bg-blue-600 text-white border-blue-600 hover:bg-blue-700"
                                : ""
                            }`}
                          >
                            {i}
                          </Button>,
                        );
                      }

                      if (endPage < meta.totalPages) {
                        if (endPage < meta.totalPages - 1) {
                          pages.push(
                            <span
                              key="ellipsis2"
                              className="px-1 text-gray-500 text-xs"
                            >
                              ...
                            </span>,
                          );
                        }

                        pages.push(
                          <Button
                            key={meta.totalPages}
                            variant="outline"
                            size="sm"
                            onClick={() => goToPage(meta.totalPages)}
                            className={`px-2 py-1 min-w-[28px] text-xs ${
                              meta.currentPage === meta.totalPages
                                ? "bg-blue-50 text-blue-600 border-blue-200"
                                : ""
                            }`}
                          >
                            {meta.totalPages}
                          </Button>,
                        );
                      }

                      return pages;
                    })()}

                    <Button
                      variant="outline"
                      size="sm"
                      iconName="ChevronRight"
                      onClick={() => goToPage(meta.currentPage + 1)}
                      disabled={meta.currentPage === meta.totalPages}
                      className="px-2 py-1 text-xs"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  {modalMode === "add" ? "Add New Patient" : "Edit Patient"}
                </h3>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <Icon name="X" size={20} />
                </button>
              </div>

              {modalMode === "edit" && (
                <p className="text-sm text-gray-600 mt-1">
                  Editing: {selectedPatient?.fullName}
                </p>
              )}
            </div>

            <form
              onSubmit={handleSavePatient}
              className="overflow-y-auto max-h-[70vh]"
            >
              <div className="p-6 space-y-4">
                {formError && (
                  <div className="bg-red-50 border border-red-200 rounded-md p-3">
                    <p className="text-red-700 text-sm">{formError}</p>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Name *
                    </label>
                    <input
                      type="text"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter patient name"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Contact No *
                    </label>
                    <input
                      type="text"
                      name="contactNo"
                      value={formData.contactNo}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter contact number"
                      inputMode="numeric"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Age *
                    </label>
                    <input
                      type="text"
                      name="age"
                      value={formData.age}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter age"
                      inputMode="numeric"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Gender *
                    </label>
                    <select
                      name="gender"
                      value={formData.gender}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {genderOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Email (Optional)
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter email"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Date of Birth (Optional)
                    </label>
                    <input
                      type="date"
                      name="dob"
                      value={formData.dob}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Passport (Optional)
                    </label>
                    <input
                      type="text"
                      name="passport"
                      value={formData.passport}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter passport number"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Address (Optional)
                    </label>
                    <textarea
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter address"
                    />
                  </div>
                </div>
              </div>

              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={closeModal}
                  className="text-sm"
                >
                  Cancel
                </Button>

                <Button
                  type="submit"
                  variant="solid"
                  size="sm"
                  disabled={isSubmitting}
                  className="bg-blue-600 hover:bg-blue-700 text-white text-sm disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      {modalMode === "add" ? "Adding..." : "Updating..."}
                    </>
                  ) : modalMode === "add" ? (
                    "Add Patient"
                  ) : (
                    "Update Patient"
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientManagement;